/**
 * Canonical definition of which CSV column headers the batch-report importers
 * actually ingest. This is the single source of truth shared by:
 *   - the workflows-service importers (services/.../business-report.service.ts and
 *     point-of-sale.service.ts), which use it to map headers -> schema keys, and
 *   - the backoffice-v2 upload validators, which use it to warn the user about
 *     headers whose data would be silently dropped.
 *
 * The two families intentionally differ — this mirrors the live backend exactly:
 *   - Digital Footprint matches scalar headers by EXACT, case-sensitive string and
 *     indexed headers by case-insensitive regex that REQUIRES the "(Optional)"
 *     suffix. A header missing the annotation is dropped (ONC-52: AFS lost almost
 *     all declaredDetails by stripping the annotations).
 *   - Point of Sale matches scalar headers by case-insensitive substring and
 *     indexed headers by regex that does NOT require any annotation — so it is
 *     tolerant of stripped annotations ("POS worked" in the same report).
 */

// ---------------------------------------------------------------------------
// Digital Footprint (strict — exact match, "(Optional)" required)
// ---------------------------------------------------------------------------

/**
 * Exact (case-sensitive) scalar header -> declaredDetails schema key. Mirrors
 * `digitalFootprintCsvHeaderToSchemaKeyMap` in business-report.service.ts.
 */
export const DIGITAL_FOOTPRINT_BATCH_HEADER_TO_SCHEMA_KEY = {
  'Website URL (Mandatory)': 'websiteUrl',
  'Website URL (Required)': 'websiteUrl',
  'Website URL': 'websiteUrl',
  'Merchant ID (Optional)': 'correlationId',
  'Merchant Legal Name (Optional - Required for MMSP)': 'merchantName',
  'Merchant Name (Optional)': 'merchantName',
  'Address (Optional - Required for MMSP)': 'address',
  'Address 1 (Optional)': 'address',
  'MCC (Optional - Required for MMSP)': 'mcc',
  'MCC (Optional)': 'mcc',
  'Additional URLs (Optional - Required for MMSP)': 'additionalUrls',
  'Registration Number (Optional)': 'registrationNumber',
  'Country (Optional)': 'countryCode',
  'State (Optional)': 'state',
  'Additional Info (Optional)': 'additionalInformation',
  'Line of Business (Optional)': 'lineOfBusiness',
  'ICA/BIN (Optional)': 'icaBin',
  'ICA/BIN Name (Optional)': 'icaBinName',
  'Gated Content Username (Optional)': 'gatedContentUsername',
  'Gated Content Password (Optional)': 'gatedContentPassword',
} as const satisfies Record<string, string>;

const DIGITAL_FOOTPRINT_SCALAR_HEADERS: ReadonlySet<string> = new Set(
  Object.keys(DIGITAL_FOOTPRINT_BATCH_HEADER_TO_SCHEMA_KEY),
);

/** Indexed array families. Each requires the literal "(Optional)" suffix. */
export const DF_BATCH_ADDRESS_HEADER_PATTERN = /^Address (\d+) \(Optional\)$/i;
export const DF_BATCH_EMAIL_HEADER_PATTERN = /^Email (\d+) \(Optional\)$/i;
export const DF_BATCH_PHONE_HEADER_PATTERN = /^Phone (\d+) \(Optional\)$/i;
export const DF_BATCH_BILLING_DESCRIPTOR_HEADER_PATTERN =
  /^Billing Descriptor (\d+) \(Optional\)$/i;
export const DF_BATCH_DBA_HEADER_PATTERN = /^DBA (\d+) \(Optional\)$/i;

export type StakeholderHeaderLabel = 'UBO' | 'Director';
export type StakeholderHeaderField = 'Name' | 'Role' | 'Address';

/** UBO/Director N {Name|Role|Address} (Optional). Mirrors extractStakeholders. */
export const dfBatchStakeholderHeaderPattern = (
  label: StakeholderHeaderLabel,
  field: StakeholderHeaderField,
): RegExp => new RegExp(`^${label} (\\d+) ${field} \\(Optional\\)$`, 'i');

const STAKEHOLDER_LABELS: readonly StakeholderHeaderLabel[] = ['UBO', 'Director'];
const STAKEHOLDER_FIELDS: readonly StakeholderHeaderField[] = ['Name', 'Role', 'Address'];

const DIGITAL_FOOTPRINT_INDEXED_PATTERNS: readonly RegExp[] = [
  DF_BATCH_ADDRESS_HEADER_PATTERN,
  DF_BATCH_EMAIL_HEADER_PATTERN,
  DF_BATCH_PHONE_HEADER_PATTERN,
  DF_BATCH_BILLING_DESCRIPTOR_HEADER_PATTERN,
  DF_BATCH_DBA_HEADER_PATTERN,
  ...STAKEHOLDER_LABELS.flatMap(label =>
    STAKEHOLDER_FIELDS.map(field => dfBatchStakeholderHeaderPattern(label, field)),
  ),
];

export const isDigitalFootprintBatchHeaderRecognized = (header: string): boolean => {
  const trimmed = header.trim();

  if (trimmed.length === 0) {
    return false;
  }

  if (DIGITAL_FOOTPRINT_SCALAR_HEADERS.has(trimmed)) {
    return true;
  }

  return DIGITAL_FOOTPRINT_INDEXED_PATTERNS.some(pattern => pattern.test(trimmed));
};

export const findUnrecognizedDfBatchHeaders = (headers: string[]): string[] =>
  headers
    .map(header => header.trim())
    .filter(header => header.length > 0 && !isDigitalFootprintBatchHeaderRecognized(header));

// ---------------------------------------------------------------------------
// Point of Sale (lenient — substring match, annotation optional)
// ---------------------------------------------------------------------------

/**
 * Scalar matchers (case-insensitive substring). Mirrors `COLUMN_MATCHERS` in
 * point-of-sale.service.ts; order is preserved for the backend's first-match
 * claiming, but it does not affect recognition.
 */
export const POS_BATCH_COLUMN_MATCHERS = [
  { key: 'companyName', match: 'Company Name' },
  { key: 'countryCode', match: 'Country' },
  { key: 'registrationNumber', match: 'Registration Number' },
  { key: 'state', match: 'State' },
  { key: 'correlationId', match: 'Merchant ID' },
  { key: 'websiteUrl', match: 'Website URL' },
  { key: 'mcc', match: 'MCC' },
  { key: 'icaBinName', match: 'ICA/BIN Name' },
  { key: 'icaBin', match: 'ICA/BIN' },
  { key: 'additionalInformation', match: 'Additional Info' },
] as const;

/** Indexed array families — no "(Optional)" required, tolerant of leading space. */
export const POS_BATCH_ADDRESS_HEADER_PATTERN = /^\s*Address\b/i;
export const POS_BATCH_EMAIL_HEADER_PATTERN = /^\s*Email\s+\d+/i;
export const POS_BATCH_PHONE_HEADER_PATTERN = /^\s*Phone\s+\d+/i;
export const POS_BATCH_DBA_HEADER_PATTERN = /^\s*DBA\s+\d+/i;
export const POS_BATCH_BILLING_DESCRIPTOR_HEADER_PATTERN = /^\s*Billing Descriptor\s+\d+/i;

/** UBO/Director N {Name|Role|Address} — prefix match, no annotation. */
export const posBatchStakeholderHeaderPattern = (
  label: StakeholderHeaderLabel,
  field: StakeholderHeaderField,
): RegExp => new RegExp(`^\\s*${label}\\s+(\\d+)\\s+${field}\\b`, 'i');

const POINT_OF_SALE_INDEXED_PATTERNS: readonly RegExp[] = [
  POS_BATCH_ADDRESS_HEADER_PATTERN,
  POS_BATCH_EMAIL_HEADER_PATTERN,
  POS_BATCH_PHONE_HEADER_PATTERN,
  POS_BATCH_DBA_HEADER_PATTERN,
  POS_BATCH_BILLING_DESCRIPTOR_HEADER_PATTERN,
  ...STAKEHOLDER_LABELS.flatMap(label =>
    STAKEHOLDER_FIELDS.map(field => posBatchStakeholderHeaderPattern(label, field)),
  ),
];

export const isPointOfSaleBatchHeaderRecognized = (header: string): boolean => {
  const trimmed = header.trim();

  if (trimmed.length === 0) {
    return false;
  }

  if (POS_BATCH_COLUMN_MATCHERS.some(({ match }) => trimmed.includes(match))) {
    return true;
  }

  return POINT_OF_SALE_INDEXED_PATTERNS.some(pattern => pattern.test(trimmed));
};

export const findUnrecognizedPosBatchHeaders = (headers: string[]): string[] =>
  headers
    .map(header => header.trim())
    .filter(header => header.length > 0 && !isPointOfSaleBatchHeaderRecognized(header));
